import type {
  NotesInterface,
  UnitInfoBaseInterface,
  UnitInfoInterface,
} from 'store/units/types';
import { StringInput } from './StringInput';
import { InputAdornment, IconButton, Button } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import type { SelectedUnitProps } from 'containers/PropertyPanel/components/withSelectedUnit';
import { withSelectedUnit } from 'containers/PropertyPanel/components/withSelectedUnit';
import { useEditUnitInfo } from 'containers/PropertyPanel/util/hooks/useEditUnitInfo';
import PropertyPanelAccordion from 'containers/PropertyPanel/components/PropertyPanelAccordion';
import { SwitchWithLabel } from './SwitchWithLabel';
import { ReactComponent as AddCircleIcon } from 'icons/addCircle.svg';
import { useTheme } from '@emotion/react';

type Note = keyof NotesInterface;

const Notes = <T extends Extract<UnitInfoInterface, UnitInfoBaseInterface>>({
  id,
  unitInfo,
}: SelectedUnitProps<T>) => {
  const theme = useTheme();
  const { setValues } = useEditUnitInfo(id, unitInfo);

  const editNote = (note: keyof NotesInterface) => (val?: string) => {
    setValues({
      ...unitInfo,
      notes: {
        ...unitInfo.notes,
        [note]: val,
      },
    });
  };

  const editDisplayValue = (note: keyof NotesInterface) => (val: boolean) => {
    setValues({
      ...unitInfo,
      display: {
        ...unitInfo.display,
        displayNotes: {
          ...unitInfo.display.displayNotes,
          [note]: val,
        },
      },
    });
  };

  const {
    notes,
    display: { displayNotes },
  } = unitInfo;

  const numberOfNotes =
    (notes && Object.values(notes).filter((v) => v !== undefined)?.length) ?? 0;

  const currentNotes = notes
    ? Object.entries(notes)
        .filter(([_key, val]) => val !== undefined)
        .map((k) => k[0])
    : [];

  const noteKeys: Note[] = [
    'noteOne',
    'noteTwo',
    'noteThree',
    'noteFour',
    'noteFive',
  ];

  const nextNote = noteKeys[currentNotes.length];

  return (
    <PropertyPanelAccordion title='Comments'>
      {noteKeys.map(
        (note, index) =>
          numberOfNotes > index && (
            <div key={note}>
              <SwitchWithLabel
                value={displayNotes[note]}
                label={`Comment ${index + 1}`}
                setValue={editDisplayValue(note)}
              />
              <StringInput
                key={note}
                value={notes?.[note]}
                setValue={editNote(note)}
                label={`Comment ${index + 1}`}
                endAdornment={
                  numberOfNotes === index + 1 && (
                    <InputAdornment position='end'>
                      <IconButton
                        edge='end'
                        size='small'
                        onClick={() => {
                          editNote(note)(undefined);
                        }}
                      >
                        <CloseIcon />
                      </IconButton>
                    </InputAdornment>
                  )
                }
              />
            </div>
          ),
      )}
      <Button
        variant='outlined'
        color='tertiary'
        fullWidth
        startIcon={<AddCircleIcon />}
        sx={{ marginTop: theme.spacing(1) }}
        onClick={() => editNote(nextNote)('')}
      >
        New comment
      </Button>
    </PropertyPanelAccordion>
  );
};

export default withSelectedUnit(Notes);
